import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';

interface ExpandableTextProps {
  text: string;
  maxLines?: number;
  className?: string;
  label?: string;
  labelClassName?: string;
}

const ExpandableText: React.FC<ExpandableTextProps> = ({
  text,
  maxLines = 4,
  className = '',
  label = '',
  labelClassName = ''
}) => {
  const [isOverflow, setIsOverflow] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        const { scrollHeight, clientHeight } = textRef.current;
        setIsOverflow(scrollHeight > clientHeight);
      }
    };
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [text, maxLines]);

  const lineClampStyle: React.CSSProperties = {
    display: '-webkit-box',
    WebkitLineClamp: maxLines,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word'
  };

  return (
    <>
      <div className="relative">
        {label && (
          <span className={labelClassName}>{label}</span>
        )}
        <div
          ref={textRef}
          style={lineClampStyle}
          className={className}
        >
          {text}
        </div>
        {isOverflow && (
          <button
            onClick={() => setShowModal(true)}
            className="absolute bottom-0 right-0 text-blue-400 hover:text-blue-300 text-xs font-medium cursor-pointer bg-inherit pl-2 pb-0.5 transition-colors"
            style={{
              background: 'linear-gradient(to right, transparent, var(--bg-color, rgba(15, 23, 42, 0.9)) 30%)'
            }}
          >
            ...展开
          </button>
        )}
      </div>

      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-slate-800 border border-white/10 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <span className="text-2xl">🤖</span>
                {label ? label.replace(':', '') : 'AI实战项目'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 rounded-full hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
              <p className="text-gray-200 text-sm leading-relaxed whitespace-pre-wrap">
                {text}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExpandableText;
